import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Progress } from "@/shared/ui/progress";
import { Button } from "@/shared/ui/button";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  FlaskConical,
  Layers,
  LineChart,
  Pause,
  Play,
  RotateCcw,
  Server,
  Settings,
  StopCircle,
  Timer,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import type { TrainingJob, TrainingHistoryPoint } from "../types";

interface TrainingTabProps {
  trainingJobs: TrainingJob[];
  trainingHistory: TrainingHistoryPoint[];
  showMockData: boolean;
}

export function TrainingTab({ trainingJobs, trainingHistory, showMockData }: TrainingTabProps) {
  return (
    <div className="space-y-4">
      {/* Training Queue Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select defaultValue="all">
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Jobs</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button>
          <FlaskConical className="h-4 w-4 mr-2" />
          New Training Job
        </Button>
      </div>

      {/* Training Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Jobs</p>
                <p className="text-2xl font-bold">1</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Play className="h-5 w-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Queued</p>
                <p className="text-2xl font-bold">1</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-warning/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">GPU Utilization</p>
                <p className="text-2xl font-bold">78%</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-chart-2/10 flex items-center justify-center">
                <Server className="h-5 w-5 text-chart-2" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Training Time</p>
                <p className="text-2xl font-bold">2.4h</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-success/10 flex items-center justify-center">
                <Timer className="h-5 w-5 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Training Job Detail */}
      {showMockData && trainingJobs.filter(j => j.status === 'running').map((job) => (
        <Card key={job.id} className="border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <FlaskConical className="h-5 w-5 text-primary animate-pulse" />
                </div>
                <div>
                  <CardTitle className="text-lg">{job.name}</CardTitle>
                  <CardDescription>{job.dataset} • {job.station}</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm">
                  <Pause className="h-4 w-4 mr-1" />
                  Pause
                </Button>
                <Button variant="destructive" size="sm">
                  <StopCircle className="h-4 w-4 mr-1" />
                  Stop
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Progress Section */}
              <div className="lg:col-span-2 space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Training Progress</span>
                    <span className="text-sm text-muted-foreground">
                      Epoch {job.epoch}/{job.totalEpochs} • {job.progress}%
                    </span>
                  </div>
                  <Progress value={job.progress} className="h-3" />
                </div>
                
                {/* Training Metrics Visualization */}
                <Card className="bg-background/50">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <LineChart className="h-4 w-4" />
                      Training Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-3">
                    <div className="h-32 flex items-end justify-between gap-1 px-2">
                      {trainingHistory.map((point, i) => (
                        <div key={i} className="flex flex-col items-center gap-1 flex-1">
                          <div 
                            className="w-full bg-primary/20 rounded-t relative overflow-hidden"
                            style={{ height: `${point.accuracy}%` }}
                          >
                            <div 
                              className="absolute bottom-0 w-full bg-primary transition-all"
                              style={{ height: `${point.accuracy}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-muted-foreground">{point.epoch}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-center gap-6 mt-3 text-xs">
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-2 rounded-full bg-primary" />
                        <span className="text-muted-foreground">Accuracy</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Live Metrics */}
              <div className="space-y-3">
                <Card className="bg-background/50">
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Current Loss</span>
                        <span className="font-mono text-sm font-medium text-success">{job.loss}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Accuracy</span>
                        <span className="font-mono text-sm font-medium">{job.accuracy}%</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Learning Rate</span>
                        <span className="font-mono text-sm">0.0001</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Batch Size</span>
                        <span className="font-mono text-sm">64</span>
                      </div>
                      <div className="flex items-center justify-between border-t pt-3">
                        <span className="text-sm text-muted-foreground">ETA</span>
                        <span className="font-mono text-sm text-primary">{job.eta}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-background/50">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Model Architecture</span>
                      <Badge variant="outline">ResNet-50</Badge>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-muted-foreground">Parameters</span>
                      <span className="font-mono text-sm">25.6M</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Training Queue Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Training Queue
          </CardTitle>
          <CardDescription>All training jobs and their status</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Model Name</TableHead>
                <TableHead>Dataset</TableHead>
                <TableHead>Station</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Accuracy</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showMockData ? trainingJobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell className="font-medium">{job.name}</TableCell>
                  <TableCell className="text-muted-foreground">{job.dataset}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{job.station}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={job.progress} className="w-20 h-2" />
                      <span className="text-xs text-muted-foreground w-8">{job.progress}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {job.accuracy ? (
                      <span
                        className={`font-mono text-sm ${
                          job.accuracy >= 90 ? "text-success" : job.accuracy >= 70 ? "text-warning" : "text-destructive"
                        }`}
                      >
                        {job.accuracy}%
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      job.status === 'running' ? 'default' :
                      job.status === 'completed' ? 'secondary' :
                      job.status === 'queued' ? 'outline' : 'destructive'
                    }>
                      {job.status === 'running' && <Play className="h-3 w-3 mr-1" />}
                      {job.status === 'completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                      {job.status === 'queued' && <Clock className="h-3 w-3 mr-1" />}
                      {job.status === 'failed' && <AlertTriangle className="h-3 w-3 mr-1" />}
                      {job.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {job.status === 'completed' && (
                        <Button variant="ghost" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                      {job.status === 'failed' && (
                        <Button variant="ghost" size="sm">
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm">
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No training jobs available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
